import { useLocation, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsShield, BsPauseCircle } from 'react-icons/bs';

import AddonUpsellPanel from '#globalComponents/addons/AddonUpsellPanel';
import GateScreen from '#globalComponents/GateScreen/GateScreen';
import { useAuth } from '#globalContexts/AuthProvider';
import { ADDON_CONFIGS } from '#lib/addons/addonConfigs';
import { BILLING_SETTINGS_PATH } from '#lib/constants/routes';
import { formatDate } from '#lib/dateUtils';
import { useBillingStatus } from '#settingsFeature/hooks/useBillingStatus';

const NEXT_STEPS = [
	'Überprüfen Sie Ihr aktuelles Abonnement',
	'Kontaktieren Sie Ihren Administrator für Berechtigungen',
	'Prüfen Sie die verfügbaren Features in Ihrem Plan',
];

const PausedPanel = ({ resumesAt, isOwner }) => {
	const navigate = useNavigate();

	const untilText = resumesAt ? `Bis zum ${formatDate(resumesAt)} sind` : 'Während der Pause sind';
	const ownerText = isOwner
		? ' – Sie können Ihr Abonnement jederzeit vorzeitig fortsetzen.'
		: '. Bitte wenden Sie sich an den Eigentümer Ihrer Organisation, um es fortzusetzen.';

	return (
		<GateScreen
			icon={BsPauseCircle}
			title="Ihr Abonnement ist pausiert"
			action={
				isOwner ? { label: 'Abo jetzt fortsetzen', onClick: () => navigate(BILLING_SETTINGS_PATH) } : undefined
			}
		>
			<p className="text-sm text-text">
				{untilText} Lead-Recherche, Versand und Export deaktiviert. Ihre Daten bleiben erhalten und sichtbar
				{ownerText}
			</p>
		</GateScreen>
	);
};

PausedPanel.propTypes = {
	resumesAt: PropTypes.string,
	isOwner: PropTypes.bool.isRequired,
};

const NotAllowedPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { orgPlan, orgRole } = useAuth();
	const { pause } = useBillingStatus();

	// A pause gates every capability at once, so it wins over the add-on-specific upsell.
	if (orgPlan === 'paused') {
		return <PausedPanel resumesAt={pause?.resumesAt ?? null} isOwner={orgRole === 'owner'} />;
	}

	// Add-on-specific upsell instead of the generic wall when the user was gated from an add-on.
	// The gate (ProtectedAppRoute) passes the addonKey via location.state.addon.
	const gatedAddon = location.state?.addon ? ADDON_CONFIGS[location.state.addon] : null;
	if (gatedAddon) {
		return <AddonUpsellPanel addon={gatedAddon} />;
	}

	return (
		<GateScreen
			icon={BsShield}
			title="Zugriff nicht erlaubt"
			action={{ label: 'Abonnement verwalten', onClick: () => navigate(BILLING_SETTINGS_PATH) }}
		>
			<p className="text-sm text-text">
				Möglicherweise müssen Sie auf ein höheres Paket upgraden oder haben nicht die nötigen Berechtigungen.
			</p>
			<div>
				<p className="text-sm-bold text-text">Was können Sie tun?</p>
				<ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-text">
					{NEXT_STEPS.map((step) => (
						<li key={step}>{step}</li>
					))}
				</ul>
			</div>
		</GateScreen>
	);
};

export default NotAllowedPage;
